import { calculateWacc, type WaccInput, type WaccResult } from "./wacc";
import { US_MARGINAL_TAX_RATE } from "../data/marginal-tax-rates";
import { validateDcfInput } from "../validation";

const PROJECTED_TAX_RATE = 0.26;
const DEFAULT_LONG_TERM_GROWTH = 0.035;
const GORDON_GROWTH_MIN_SPREAD = 0.005;

export interface DcfHistoricalYear {
  revenue: number;
  earningsBeforeTax: number;
  interestExpense: number;
  incomeTaxPaid: number;
  depreciation: number;
  cash: number;
  currentAssets: number;
  currentLiabilities: number;
  ppeBeginning: number;
  ppeEnd: number;
  sdeExclDepreciation?: number;
  loanNetBorrowings?: number;
}

export interface DcfProjectionAssumptions {
  revenueGrowthPct: number;
  operatingMarginPct: number;
  depreciationPct?: number;
  capexPct: number;
  nwcPct: number;
  sdePct?: number;
  interestExpenses?: number;
  netBorrowings?: number;
}

export interface DcfWaccInputs {
  industry: string;
  costOfDebt?: number;
  isPrivatelyOwned: boolean;
  hasCustomerConcentration: boolean;
  hasHighCompetition: boolean;
  hasVolatileCashFlow: boolean;
  ownerHoursPerWeek: number;
  yearsOperating: number;
  hasSustainableAdvantage: boolean;
  riskFreeRate?: number;
  waccDirectOverride?: number;
}

export interface DcfValuationInput {
  historicalYears: DcfHistoricalYear[];
  projectionAssumptions: DcfProjectionAssumptions[];
  waccInputs: DcfWaccInputs;
  longTermGrowthRate?: number;
  /** @internal Used by sensitivity analysis to override the computed WACC */
  _waccOverride?: number;
  /** Set to true to skip input validation. Default: false. */
  skipValidation?: boolean;
  /** Override the projected tax rate applied to EBIT (default: 0.26) */
  projectedTaxRate?: number;
}

export interface DcfScenarioInput {
  label: string;
  projectionAssumptions: DcfProjectionAssumptions[];
  longTermGrowthRate?: number;
}

export interface DcfScenarioResult {
  label: string;
  result: DcfValuationResult;
}

export interface DcfHistoricalOutput {
  revenue: number;
  revenueGrowth: number | null;
  ebit: number;
  operatingMargin: number;
  taxOnOperatingIncome: number;
  nopat: number;
  depreciation: number;
  nwcChange: number | null;
  capex: number;
  fcff: number | null;
  afterTaxInterest: number;
  loanNetBorrowings: number;
  sdeExclDepreciation: number;
  fcfe: number | null;
}

export interface DcfProjectionOutput {
  revenue: number;
  revenueGrowth: number;
  ebit: number;
  operatingMargin: number;
  taxOnOperatingIncome: number;
  nopat: number;
  depreciation: number;
  nwcChange: number;
  capex: number;
  fcff: number;
  afterTaxInterest: number;
  loanNetBorrowings: number;
  sdeExclDepreciation: number;
  fcfe: number;
}

export interface DcfValuationResult {
  enterpriseValue: number;
  equityValue: number;
  wacc: WaccResult;
  historicalOutputs: DcfHistoricalOutput[];
  projectionOutputs: DcfProjectionOutput[];
  terminalValueFcff: number;
  terminalValueFcfe: number;
  pvFcff: number;
  pvTerminalFcff: number;
  pvFcfe: number;
  pvTerminalFcfe: number;
  warnings: string[];
}

function computeHistoricalOutputs(
  years: DcfHistoricalYear[],
): DcfHistoricalOutput[] {
  const outputs: DcfHistoricalOutput[] = [];

  for (let i = 0; i < years.length; i++) {
    const y = years[i];

    const ebit = y.earningsBeforeTax + y.interestExpense;
    const operatingMargin = y.revenue !== 0 ? ebit / y.revenue : 0;

    const effectiveTaxRate =
      y.earningsBeforeTax !== 0 ? y.incomeTaxPaid / y.earningsBeforeTax : 0;
    const taxOnOperatingIncome = ebit * effectiveTaxRate;
    const nopat = ebit - taxOnOperatingIncome;

    let nwcChange: number | null = null;
    if (i > 0) {
      const prev = years[i - 1];
      const prevNwcExCash =
        prev.currentAssets - prev.cash - prev.currentLiabilities;
      const currNwcExCash =
        y.currentAssets - y.cash - y.currentLiabilities;
      nwcChange = -(currNwcExCash - prevNwcExCash);
    }

    const capex = -(y.ppeEnd - y.ppeBeginning + y.depreciation);

    let fcff: number | null = null;
    if (i > 0) {
      fcff = nopat + y.depreciation + (nwcChange ?? 0) + capex;
    }

    const afterTaxInterest =
      -y.interestExpense * (1 - effectiveTaxRate);

    let fcfe: number | null = null;
    if (i > 0 && fcff !== null) {
      fcfe =
        fcff +
        afterTaxInterest +
        (y.loanNetBorrowings ?? 0) +
        (y.sdeExclDepreciation ?? 0);
    }

    outputs.push({
      revenue: y.revenue,
      revenueGrowth:
        i > 0 ? years[i].revenue / years[i - 1].revenue - 1 : null,
      ebit,
      operatingMargin,
      taxOnOperatingIncome,
      nopat,
      depreciation: y.depreciation,
      nwcChange,
      capex,
      fcff,
      afterTaxInterest,
      loanNetBorrowings: y.loanNetBorrowings ?? 0,
      sdeExclDepreciation: y.sdeExclDepreciation ?? 0,
      fcfe,
    });
  }

  return outputs;
}

function computeHistoricalAverageDepreciationPct(
  years: DcfHistoricalYear[],
): number {
  let sum = 0;
  for (const y of years) {
    sum += y.revenue !== 0 ? y.depreciation / y.revenue : 0;
  }
  return years.length > 0 ? sum / years.length : 0;
}

function computeProjectionOutputs(
  historicalYears: DcfHistoricalYear[],
  historicalOutputs: DcfHistoricalOutput[],
  projections: DcfProjectionAssumptions[],
  taxRate: number = PROJECTED_TAX_RATE,
): DcfProjectionOutput[] {
  const lastHistorical = historicalYears[historicalYears.length - 1];
  const lastHistOutput = historicalOutputs[historicalOutputs.length - 1];
  const avgDepPct = computeHistoricalAverageDepreciationPct(historicalYears);

  const outputs: DcfProjectionOutput[] = [];
  let prevRevenue = lastHistOutput.revenue;
  let prevNwcExCashRatio =
    lastHistorical.revenue !== 0
      ? (lastHistorical.currentAssets -
          lastHistorical.cash -
          lastHistorical.currentLiabilities) /
        lastHistorical.revenue
      : 0;

  for (let i = 0; i < projections.length; i++) {
    const p = projections[i];
    const revenue = prevRevenue * (1 + p.revenueGrowthPct);
    const ebit = revenue * p.operatingMarginPct;
    const taxOnOperatingIncome = ebit * taxRate;
    const nopat = ebit - taxOnOperatingIncome;
    const depPct = p.depreciationPct ?? avgDepPct;
    const depreciation = revenue * depPct;

    const prevNwcProduct =
      i === 0 ? prevRevenue * prevNwcExCashRatio : outputs[i - 1].revenue * p.nwcPct;
    const nwcChange = -(revenue * p.nwcPct - prevNwcProduct);
    const capex = -(revenue * p.capexPct);

    const fcff = nopat + depreciation + nwcChange + capex;

    const interestExpenses = p.interestExpenses ?? 0;
    const afterTaxInterest = -interestExpenses * (1 - taxRate);
    const netBorrowings = p.netBorrowings ?? 0;
    const sdePct = p.sdePct ?? 0;
    const sde = revenue * sdePct;

    const fcfe = fcff + afterTaxInterest + netBorrowings + sde;

    outputs.push({
      revenue,
      revenueGrowth: p.revenueGrowthPct,
      ebit,
      operatingMargin: p.operatingMarginPct,
      taxOnOperatingIncome,
      nopat,
      depreciation,
      nwcChange,
      capex,
      fcff,
      afterTaxInterest,
      loanNetBorrowings: netBorrowings,
      sdeExclDepreciation: sde,
      fcfe,
    });

    prevRevenue = revenue;
    prevNwcExCashRatio = p.nwcPct;
  }

  return outputs;
}

function safeTerminalValue(
  lastCashFlow: number,
  discountRate: number,
  growthRate: number,
  warnings: string[],
): number {
  const spread = discountRate - growthRate;

  if (spread < GORDON_GROWTH_MIN_SPREAD) {
    warnings.push(
      `Gordon Growth spread (${(spread * 100).toFixed(2)}%) is below minimum threshold ` +
      `(${(GORDON_GROWTH_MIN_SPREAD * 100).toFixed(2)}%); terminal value capped using minimum spread`,
    );
    return (lastCashFlow * (1 + growthRate)) / GORDON_GROWTH_MIN_SPREAD;
  }

  if (lastCashFlow < 0) {
    warnings.push(
      "Terminal cash flow is negative; terminal value set to zero " +
      "(the business is not generating positive free cash flow in the final projection year)",
    );
    return 0;
  }

  return (lastCashFlow * (1 + growthRate)) / spread;
}

export function calculateDcfValuation(
  input: DcfValuationInput,
): DcfValuationResult {
  const warnings: string[] = [];

  if (!input.skipValidation) {
    validateDcfInput({
      historicalYearsCount: input.historicalYears.length,
      projectionYearsCount: input.projectionAssumptions.length,
      longTermGrowthRate: input.longTermGrowthRate,
      waccOwnerHoursPerWeek: input.waccInputs.ownerHoursPerWeek,
      waccYearsOperating: input.waccInputs.yearsOperating,
    });
  }

  let waccResult: WaccResult;
  let effectiveWacc: number;

  if (input._waccOverride !== undefined) {
    const waccInput: WaccInput = {
      ...input.waccInputs,
      useMarginalTaxRate: true,
      marginalTaxRate: US_MARGINAL_TAX_RATE,
    };
    waccResult = calculateWacc(waccInput);
    effectiveWacc = input._waccOverride;
  } else if (input.waccInputs.waccDirectOverride !== undefined) {
    const waccInput: WaccInput = {
      ...input.waccInputs,
      useMarginalTaxRate: true,
      marginalTaxRate: US_MARGINAL_TAX_RATE,
    };
    waccResult = calculateWacc(waccInput);
    effectiveWacc = input.waccInputs.waccDirectOverride;
  } else {
    const waccInput: WaccInput = {
      ...input.waccInputs,
      useMarginalTaxRate: true,
      marginalTaxRate: US_MARGINAL_TAX_RATE,
    };
    waccResult = calculateWacc(waccInput);
    effectiveWacc = waccResult.wacc;
  }

  const historicalOutputs = computeHistoricalOutputs(input.historicalYears);
  const projectionOutputs = computeProjectionOutputs(
    input.historicalYears,
    historicalOutputs,
    input.projectionAssumptions,
    input.projectedTaxRate,
  );

  const longTermGrowth =
    input.longTermGrowthRate ?? DEFAULT_LONG_TERM_GROWTH;
  const lastProj = projectionOutputs[projectionOutputs.length - 1];

  const terminalValueFcff = safeTerminalValue(
    lastProj.fcff,
    effectiveWacc,
    longTermGrowth,
    warnings,
  );
  const terminalValueFcfe = safeTerminalValue(
    lastProj.fcfe,
    waccResult.costOfEquity,
    longTermGrowth,
    warnings,
  );

  let pvFcff = 0;
  let pvFcfe = 0;
  const numYears = projectionOutputs.length;

  for (let i = 0; i < numYears; i++) {
    const period = i + 1;
    const discountFactorWacc = 1 / (1 + effectiveWacc) ** period;
    const discountFactorEquity =
      1 / (1 + waccResult.costOfEquity) ** period;

    pvFcff += projectionOutputs[i].fcff * discountFactorWacc;
    pvFcfe += projectionOutputs[i].fcfe * discountFactorEquity;
  }

  const terminalDiscountWacc =
    1 / (1 + effectiveWacc) ** numYears;
  const terminalDiscountEquity =
    1 / (1 + waccResult.costOfEquity) ** numYears;

  const pvTerminalFcff = terminalValueFcff * terminalDiscountWacc;
  const pvTerminalFcfe = terminalValueFcfe * terminalDiscountEquity;

  const enterpriseValue = pvFcff + pvTerminalFcff;
  const equityValue = pvFcfe + pvTerminalFcfe;

  return {
    enterpriseValue,
    equityValue,
    wacc: waccResult,
    historicalOutputs,
    projectionOutputs,
    terminalValueFcff,
    terminalValueFcfe,
    pvFcff,
    pvTerminalFcff,
    pvFcfe,
    pvTerminalFcfe,
    warnings,
  };
}

/**
 * Run multiple scenario analyses (e.g. best/base/worst) against
 * the same historical data and WACC inputs.
 */
export function calculateDcfScenarios(
  historicalYears: DcfHistoricalYear[],
  waccInputs: DcfWaccInputs,
  scenarios: DcfScenarioInput[],
  options?: { projectedTaxRate?: number },
): DcfScenarioResult[] {
  return scenarios.map((scenario) => ({
    label: scenario.label,
    result: calculateDcfValuation({
      historicalYears,
      projectionAssumptions: scenario.projectionAssumptions,
      waccInputs,
      longTermGrowthRate: scenario.longTermGrowthRate,
      projectedTaxRate: options?.projectedTaxRate,
    }),
  }));
}
